import React, { useEffect, useState } from "react";

import { ROLE_NAME_MAP } from "@/shared/utils/maps";

import { getUserRoles, saveUserRoles } from "../services/accessService";
import type { ApprovalType, Props, Role } from "../types/access";

const ManageRoles: React.FC<Props> = ({ open, onClose, members, roles, createdBy }) => {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [showRoles, setShowRoles] = useState(false);

  const [userRolesMap, setUserRolesMap] = useState<Record<string, string[]>>({});

  const [roleMap, setRoleMap] = useState<Record<string, { role: Role; approval: ApprovalType }>>(
    {}
  );

  const [initialState, setInitialState] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredUsers = members.filter((m) => {
    const name = (m.displayName || "").toLowerCase();
    const email = (m.mail || "").toLowerCase();
    return name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
  });

  useEffect(() => {
    const loadUserRoles = async () => {
      const map: Record<string, string[]> = {};

      for (const m of members) {
        try {
          const res = await getUserRoles(m.mail);
          map[m.mail] = res.data.map((r: any) => r.role_code);
        } catch {
          map[m.mail] = [];
        }
      }

      setUserRolesMap(map);
    };

    if (members.length) {
      loadUserRoles();
    }
  }, [members]);

  useEffect(() => {
    if (!selectedUser) {
      setRoleMap({});
      setInitialState("");
      setShowRoles(false);
      return;
    }

    getUserRoles(selectedUser).then((res) => {
      const map: typeof roleMap = {};
      res.data.forEach((r: any) => {
        const role = roles.find((x) => x.role_code === r.role_code);
        if (role) {
          map[role.role_code] = {
            role,
            approval: "approver",
          };
        }
      });

      setRoleMap(map);
      setInitialState(JSON.stringify(map));
      setShowRoles(true);
    });
  }, [selectedUser, roles]);

  const handleAddUser = (user: any) => {
    setSelectedUser(user.mail);
    setSelectedUserName(user.displayName || user.mail);
  };

  const toggleRole = (role: Role) => {
    setRoleMap((prev) => {
      const next = { ...prev };
      if (next[role.role_code]) {
        delete next[role.role_code];
      } else {
        next[role.role_code] = {
          role,
          approval: "approver",
        };
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveUserRoles({
        user_principal_name: selectedUser,
        created_by: createdBy,
        roles: Object.values(roleMap).map((r) => ({
          role_code: r.role.role_code,
          approval_type: "approver",
        })),
      });
      setInitialState(JSON.stringify(roleMap));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setSelectedUser("");
    setSelectedUserName("");
    setRoleMap({});
    setShowRoles(false);
    onClose();
  };

  const hasChanges =
    selectedUser !== "" && initialState !== "" && JSON.stringify(roleMap) !== initialState;

  if (!open) return null;

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">Daraja Onboarding</h2>
            </div>

            <div className="flex items-center gap-3">
              {showRoles && (
                <>
                  {hasChanges && (
                    <div className="text-orange-600 border border-orange-300 bg-orange-50 px-3 py-1 rounded-md text-sm">
                      Unsaved
                    </div>
                  )}

                  <button
                    className="bg-[#e7e5e0] text-gray-700 px-4 py-1.5 rounded-md text-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>

                  <button
                    className="bg-[var(--color-brand)]  text-white px-4 py-1.5 rounded-md text-sm"
                    onClick={handleSave}
                    disabled={!selectedUser || saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="p-5">
            {!showRoles && (
              <>
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  className="w-full border border-gray-200 p-2 rounded mb-4 focus:outline-none focus:ring-2 focus:focus:ring-orange-400"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <div className="space-y-3 max-h-[400px] overflow-auto">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.id}
                      className="border border-gray-200 rounded-md p-4 flex justify-between items-center"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          {u.displayName || "User"}
                        </div>
                        <div className="text-xs text-gray-500">{u.mail}</div>

                        <div className="flex flex-wrap gap-1 mt-1">
                          {(userRolesMap[u.mail] || []).map((code) => (
                            <span
                              key={code}
                              className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded"
                            >
                              {ROLE_NAME_MAP[code] || code}
                            </span>
                          ))}
                        </div>
                      </div>

                      <button
                        className="bg-[var(--color-brand)]  text-white px-3 py-1.5 rounded-md text-sm"
                        onClick={() => handleAddUser(u)}
                      >
                        Assign Roles
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {showRoles && (
              <div className="border border-gray-200 rounded-md p-4">
                <div className="text-sm font-medium text-gray-800">{selectedUserName}</div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {roles.map((role) => {
                    const selected = !!roleMap[role.role_code];

                    return (
                      <div
                        key={role.role_code}
                        onClick={() => toggleRole(role)}
                        className={`px-3 py-1.5 text-sm rounded-md cursor-pointer border ${
                          selected
                            ? "bg-[#fff4ec] border-orange-300 text-orange-600"
                            : "bg-white border-gray-200 text-gray-700"
                        }`}
                      >
                        {ROLE_NAME_MAP[role.role_code] || role.role_code}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRoles;
